import React, { useState, useEffect } from 'react';
import { PlusCircle, Edit, Check, X, AlertTriangle, Archive, Clock, Calendar, ArrowLeft } from 'lucide-react';
import Layout from '../components/Layout';
import api from '../components/api';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

const AnneeAcademiquePage = () => {
  const navigate = useNavigate();
  const [annees, setAnnees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedAnnee, setSelectedAnnee] = useState(null);
  const [formData, setFormData] = useState({
    annee_debut: '',
    annee_fin: '',
    date_debut: '',
    date_fin: ''
  });
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    action: null,
    annee: null
  });

  useEffect(() => {
    fetchAnnees();
  }, []);

  const fetchAnnees = async () => {
    try {
      setLoading(true);
      const response = await api.get('/etablissement/annees-academiques/');
      setAnnees(response.data);
      console.log('Années académiques:', response.data);
    } catch (error) {
      console.error('Erreur lors de la récupération des années académiques:', error);
      toast.error('Erreur lors de la récupération des années académiques');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (annee = null) => {
    if (annee) {
      setIsEdit(true);
      setSelectedAnnee(annee);
      setFormData({
        annee_debut: annee.annee_debut,
        annee_fin: annee.annee_fin,
        date_debut: annee.date_debut ? new Date(annee.date_debut).toISOString().split('T')[0] : '',
        date_fin: annee.date_fin ? new Date(annee.date_fin).toISOString().split('T')[0] : ''
      });
    } else {
      setIsEdit(false);
      setSelectedAnnee(null);
      // Suggestion d'années pour une nouvelle année académique
      const currentYear = new Date().getFullYear();
      setFormData({
        annee_debut: currentYear,
        annee_fin: currentYear + 1,
        date_debut: '',
        date_fin: ''
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setFormData({
      annee_debut: '',
      annee_fin: '',
      date_debut: '',
      date_fin: ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    if (!formData.annee_debut || !formData.annee_fin) {
      toast.error('Les années de début et de fin sont requises');
      return false;
    }

    const anneDebut = parseInt(formData.annee_debut);
    const anneeFin = parseInt(formData.annee_fin);

    if (isNaN(anneDebut) || isNaN(anneeFin)) {
      toast.error('Les années doivent être des nombres');
      return false;
    }

    if (anneeFin <= anneDebut) {
      toast.error('L\'année de fin doit être supérieure à l\'année de début');
      return false;
    }

    if (formData.date_debut && formData.date_fin) {
      const dateDebut = new Date(formData.date_debut);
      const dateFin = new Date(formData.date_fin);

      if (dateFin <= dateDebut) {
        toast.error('La date de fin doit être postérieure à la date de début');
        return false;
      }
    }

    if (formData.date_fin && new Date(formData.date_fin) < new Date()) {
      toast.warning('La date de fin est dans le passé');
      // On continue quand même, c'est juste un avertissement
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      const dataToSend = {
        ...formData,
        annee_debut: parseInt(formData.annee_debut),
        annee_fin: parseInt(formData.annee_fin)
      };

      if (!dataToSend.date_debut) {
        delete dataToSend.date_debut;
      }
      
      if (!dataToSend.date_fin) {
        delete dataToSend.date_fin;
      }

      let response;
      if (isEdit) {
        response = await api.put(`/etablissement/annees-academiques/${selectedAnnee.id}/`, dataToSend);
        toast.success('Année académique mise à jour avec succès');
      } else {
        response = await api.post('/etablissement/annees-academiques/', dataToSend);
        toast.success('Année académique créée avec succès');
      }

      fetchAnnees();
      handleCloseDialog();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      
      // Gestion des erreurs de validation
      if (error.response && error.response.data) {
        const errors = error.response.data;
        
        if (typeof errors === 'object') {
          Object.entries(errors).forEach(([field, message]) => {
            toast.error(`${field}: ${Array.isArray(message) ? message.join(', ') : message}`);
          });
        } else {
          toast.error('Erreur lors de la sauvegarde');
        }
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } finally {
      setLoading(false);
    }
  };

  const openConfirmDialog = (title, message, action, annee) => {
    setConfirmDialog({
      open: true,
      title,
      message,
      action,
      annee
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({
      open: false,
      title: '',
      message: '',
      action: null,
      annee: null
    });
  };

  const handleSetAnneeEnCours = async (annee) => {
    try {
      setLoading(true);
      await api.post(`/etablissement/annees-academiques/${annee.id}/definir/`);
      toast.success(`${annee.annee_debut}-${annee.annee_fin} définie comme année académique en cours`);
      fetchAnnees();
      closeConfirmDialog();
    } catch (error) {
      console.error('Erreur lors de la définition de l\'année en cours:', error);
      if (error.response && error.response.data && error.response.data.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Erreur lors de la définition de l\'année en cours');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClotureAnnee = async (annee) => {
    try {
      setLoading(true);
      await api.post(`/etablissement/annees-academiques/${annee.id}/cloturer/`);
      toast.success(`Année académique ${annee.annee_debut}-${annee.annee_fin} clôturée avec succès`);
      fetchAnnees();
      closeConfirmDialog();
    } catch (error) {
      console.error('Erreur lors de la clôture de l\'année académique:', error);
      toast.error('Erreur lors de la clôture de l\'année académique');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAnnee = async (annee) => {
    try {
      setLoading(true);
      await api.delete(`/etablissement/annees-academiques/${annee.id}/`);
      toast.success(`Année académique ${annee.annee_debut}-${annee.annee_fin} supprimée avec succès`);
      fetchAnnees();
      closeConfirmDialog();
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'année académique:', error);
      toast.error('Erreur lors de la suppression de l\'année académique');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          {/* Card principale avec design professionnel */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            {/* Header avec bannière bleue */}
            <div className="bg-gradient-to-r from-[#202654] to-[#202654]/90 px-6 py-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                <div className="flex items-center text-white">
                  <button
                    onClick={() => navigate(-1)}
                    className="mr-4 p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-all duration-200 backdrop-blur-sm"
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </button>
                  <div className="bg-white/10 p-3 rounded-xl mr-4">
                    <Calendar className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold">Années Académiques</h1>
                    <p className="mt-1 text-blue-100">
                      Gérez les années académiques de votre établissement
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => handleOpenDialog()}
                  className="mt-4 md:mt-0 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-[#202654] bg-white hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white"
                >
                  <PlusCircle className="h-5 w-5 mr-2" />
                  Nouvelle année
                </button>
              </div>
            </div>

            {/* Contenu principal */}
            <div className="p-6">
              {loading && annees.length === 0 ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#202654]"></div>
                </div>
              ) : annees.length === 0 ? (
                <div className="bg-white rounded-xl shadow-md p-8 text-center">
                  <Calendar className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                  <h2 className="text-xl font-semibold text-gray-700 mb-2">Aucune année académique</h2>
                  <p className="text-gray-500 mb-6">
                    Vous n'avez pas encore créé d'année académique. Commencez par en créer une.
                  </p>
                  <button
                    onClick={() => handleOpenDialog()}
                    className="inline-flex items-center bg-[#202654] text-white px-5 py-2 rounded-lg shadow-md hover:bg-[#2d347d] transition-all duration-200"
                  >
                    <PlusCircle className="h-5 w-5 mr-2" />
                    Créer une année académique
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {annees.map((annee) => (
                    <div 
                      key={annee.id} 
                      className={`bg-white rounded-xl shadow-md overflow-hidden relative transition-all hover:shadow-lg ${
                        annee.status ? 'ring-2 ring-green-500' : ''
                      } ${annee.cloture ? 'opacity-80' : ''}`}
                    >
                      <div className="p-6">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h3 className="text-2xl font-bold text-[#202654]">{annee.libelle}</h3>
                            {annee.status && (
                              <div className="mt-2 inline-flex bg-green-500 text-white rounded-full px-3 py-1 text-xs font-medium items-center">
                                <Check className="h-3 w-3 mr-1" />
                                En cours
                              </div>
                            )}
                            {annee.cloture && (
                              <div className="mt-2 inline-flex bg-gray-500 text-white rounded-full px-3 py-1 text-xs font-medium items-center">
                                <Archive className="h-3 w-3 mr-1" />
                                Clôturée
                              </div>
                            )}
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleOpenDialog(annee)}
                              disabled={annee.cloture}
                              className={`p-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors ${
                                annee.cloture ? 'opacity-50 cursor-not-allowed' : ''
                              }`}
                            >
                              <Edit className="h-5 w-5" />
                            </button>
                          </div>
                        </div>

                        <div className="space-y-3 mb-6">
                          <div className="flex items-center text-gray-600">
                            <Calendar className="h-5 w-5 mr-2 text-gray-400" />
                            <span>
                              Date de début: {annee.date_debut ? new Date(annee.date_debut).toLocaleDateString() : 'Non définie'}
                            </span>
                          </div>
                          <div className="flex items-center text-gray-600">
                            <Calendar className="h-5 w-5 mr-2 text-gray-400" />
                            <span>
                              Date de fin: {annee.date_fin ? new Date(annee.date_fin).toLocaleDateString() : 'Non définie'}
                            </span>
                          </div>
                          <div className="flex items-center text-gray-600">
                            <Clock className="h-5 w-5 mr-2 text-gray-400" />
                            <span>
                              Dernière modification: {new Date(annee.update_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-2 mt-4">
                          {!annee.status && !annee.cloture && (
                            <button
                              onClick={() =>
                                openConfirmDialog(
                                  'Définir comme année en cours',
                                  `Êtes-vous sûr de vouloir définir ${annee.libelle} comme l'année académique en cours ?`,
                                  handleSetAnneeEnCours,
                                  annee
                                )
                              }
                              className="inline-flex items-center bg-blue-50 text-blue-700 px-3 py-1.5 rounded-md text-sm hover:bg-blue-100 transition-colors"
                            >
                              <Check className="h-4 w-4 mr-1" />
                              Définir comme actuelle
                            </button>
                          )}
                          {annee.status && !annee.cloture && (
                            <button
                              onClick={() =>
                                openConfirmDialog(
                                  'Clôturer l\'année',
                                  `Êtes-vous sûr de vouloir clôturer l'année académique ${annee.libelle} ? Cette action est irréversible.`,
                                  handleClotureAnnee,
                                  annee
                                )
                              }
                              className="inline-flex items-center bg-orange-50 text-orange-700 px-3 py-1.5 rounded-md text-sm hover:bg-orange-100 transition-colors"
                            >
                              <Archive className="h-4 w-4 mr-1" />
                              Clôturer
                            </button>
                          )}
                          {!annee.status && (
                            <button
                              onClick={() =>
                                openConfirmDialog(
                                  'Supprimer',
                                  `Êtes-vous sûr de vouloir supprimer l'année académique ${annee.libelle} ? Cette action est irréversible.`,
                                  handleDeleteAnnee,
                                  annee
                                )
                              }
                              className="inline-flex items-center bg-red-50 text-red-700 px-3 py-1.5 rounded-md text-sm hover:bg-red-100 transition-colors"
                            >
                              <X className="h-4 w-4 mr-1" />
                              Supprimer
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Dialog pour créer/modifier une année académique */}
          {dialogOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
                <div className="p-6 border-b">
                  <div className="flex justify-between items-center">
                    <h2 className="text-xl font-semibold text-gray-800">
                      {isEdit ? 'Modifier l\'année académique' : 'Ajouter une année académique'}
                    </h2>
                    <button onClick={handleCloseDialog} className="text-gray-500 hover:text-gray-700">
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                </div>
                <div className="p-6">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Année de début
                      </label>
                      <input
                        type="number"
                        name="annee_debut"
                        value={formData.annee_debut}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#202654] focus:border-transparent"
                        placeholder="2023"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Année de fin
                      </label>
                      <input
                        type="number"
                        name="annee_fin"
                        value={formData.annee_fin}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#202654] focus:border-transparent"
                        placeholder="2024"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Date de début
                      </label>
                      <input
                        type="date"
                        name="date_debut"
                        value={formData.date_debut}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#202654] focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Date de début officielle de l'année académique
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Date de fin
                      </label>
                      <input
                        type="date"
                        name="date_fin"
                        value={formData.date_fin}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#202654] focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Date de fin officielle de l'année académique (optionnelle)
                      </p>
                    </div>
                  </div>
                </div>
                <div className="p-6 border-t bg-gray-50 flex justify-end space-x-3 rounded-b-xl">
                  <button
                    onClick={handleCloseDialog}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={loading}
                    className="px-4 py-2 bg-[#202654] text-white rounded-md hover:bg-[#2d347d] transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading && (
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    )}
                    {isEdit ? 'Mettre à jour' : 'Ajouter'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Dialog de confirmation */}
          {confirmDialog.open && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <AlertTriangle className="h-6 w-6 text-orange-500 mr-2" />
                    <h2 className="text-xl font-semibold text-gray-800">{confirmDialog.title}</h2>
                  </div>
                  <p className="text-gray-600 mb-6">{confirmDialog.message}</p>
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={closeConfirmDialog}
                      className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={() => confirmDialog.action(confirmDialog.annee)}
                      disabled={loading}
                      className="px-4 py-2 bg-[#202654] text-white rounded-md hover:bg-[#2d347d] transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading && (
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      )}
                      Confirmer
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default AnneeAcademiquePage;
